import { Module } from 'vuex';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import {serializer} from '../../api/shared/serializer';
import {DigitalSignagesTypes} from "@/enums/DigitalSignagesTypes";
import {getWhereIdInIds} from "@/utils/getWhereIdInIds";
import {DigitalSignage} from "@/models/digital-signage/DigitalSignage";
import {batchUpdate} from "@/utils/batchUpdate";

const contentCreatorModule: Module<any, any> = {
    namespaced: false,
    state: {
        content: [],
        currentContent: {},
        creatorContentChanges: 0,
        contentTags: []
    },
    mutations: {
        setCreatorContent(state: any, payload: any) {
            state.content = payload;
        },
        setCurrentCreatorContent(state: any, payload: any) {
            state.currentContent = payload;
        },
        setCreatorContentChanges(state: any) {
            state.creatorContentChanges = state.creatorContentChanges + 1;
        },
        setContentTags(state: any, payload: any) {
            state.contentTags = payload;
        }
    },
    actions: {
        async getCreatorContent({commit}) {
            await firebase
                .firestore()
                .collection("creatorContent")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .get()
                .then((querySnapshot) => {
                    const content = serializer.querySnapshotToDocuments(querySnapshot);
                    commit('setCreatorContent', content)
                })
        },
        async getCurrentCreatorContent({commit}, id) {
            await firebase
                .firestore()
                .collection("creatorContent")
                .doc(id)
                .get()
                .then((querySnapshot) => {
                    const content = serializer.documentSnapshotToDocument(querySnapshot)
                    commit('setCurrentCreatorContent', content)
                })
        },
        async addCreatorContent(_, content) {
            let contentId;

            await firebase
                .firestore()
                .collection("creatorContent")
                .add(content)
                .then((data) => {
                    contentId = data.id
                });

            return contentId;
        },
        async saveCreatorContent(_, {id, data}) {
            await firebase
                .firestore()
                .collection("creatorContent")
                .doc(id)
                .update(data);
        },
        async publishCreatorContent(_, {id, data}) {
            await firebase
                .firestore()
                .collection("creatorContent")
                .doc(id)
                .update(data);
        },
        async deleteCreatorContent(_, id) {
            await firebase
                .firestore()
                .collection("creatorContent")
                .doc(id)
                .delete();
        },
        async deleteContentFromDigitalSignage(_, contentId) {
            const digitalSignages = await firebase
              .firestore()
              .collection("digitalSignage")
              .where("facilityId", "==", this.state.authModule.currentFacility)
              .get();

            const digitalSignagesDocs: DigitalSignage[] = serializer.querySnapshotToDocuments(digitalSignages);

            const digitalSignagesToUpdate = digitalSignagesDocs
              .filter(ds => ds.pages.some(page => page.contentCreatorId === contentId))
              .map(ds => ({
                  ...ds,
                  pages: ds.pages
                    .filter(page => page.contentCreatorId !== contentId)
                    .map((page, index) => ({ ...page, order: index + 1 }))
              }));

            if (digitalSignagesToUpdate.length) {
                const itemsToUpdate = digitalSignagesToUpdate.map(ds => ({
                    docId: ds.id,
                    updates: { pages: ds.pages }
                }));

                await batchUpdate('digitalSignage', itemsToUpdate);
            }
        },
        async saveContentTags({ dispatch }, {content, tagsIds}) {
            await firebase
              .firestore()
              .collection("creatorContent")
              .doc(content.id)
              .update({
                  tagsIds: tagsIds
              }).then(() => {
                  dispatch("getCurrentCreatorContent", content.id)
              })
        },
        async subscribeContentByDigitalSignage({dispatch, commit}, digitalSignage) {
            const pages = digitalSignage.pages;

            if(pages.length) {
                for(let i = 0; i < pages.length; i++) {
                    if(pages[i].type === DigitalSignagesTypes.ContentCreator) {
                        await firebase
                          .firestore()
                          .collection("creatorContent")
                          .doc(pages[i].contentCreatorId)
                          .onSnapshot(async doc => {
                              dispatch("getDigitalSignage", digitalSignage.id);
                              commit("setCreatorContentChanges")
                          })
                    }
                }
            }
        },
        async getContentTags({ commit }) {
            const snap = await firebase.firestore().collection("creatorContentTags").where('facilityId', '==', this.state.authModule.currentFacility).get()
            const tags = serializer.querySnapshotToDocuments<any>(snap)
            commit("setContentTags", tags);
        },
        async addContentTags({ dispatch }, data) {

            if (!data.tagsNames || !data.entity) return

            try {
                const existedTags = await getWhereIdInIds('name', data.tagsNames, 'creatorContentTags', this.state.authModule.currentFacility) || []

                if (existedTags?.length > 0) return {
                    isSuccess: false,
                    existedTags: existedTags.map(x => x.name)
                }

                const today = firebase.firestore.Timestamp.fromDate(new Date())

                const tagsObjects = data.tagsNames.map(name => {
                    return {
                        name: name.trim().toLowerCase(),
                        facilityId: this.state.authModule.currentFacility,
                        createdByStaffId: this.state.authModule.user.id,
                        createdDateTime: today
                    }
                })

                const addedTagsRefs = await Promise.all(tagsObjects.map(tagObj => {
                    return firebase.firestore().collection("creatorContentTags").add(tagObj);
                }));

                await dispatch("getContentTags")

                const addedTagIds = addedTagsRefs.map((tagRef: any) => tagRef.id);

                return {
                    isSuccess: true,
                    addedTagIds: addedTagIds
                }

            } catch (error) {
                return {
                    isSuccess: false
                }
            }

        },
        async updateContentTag({ dispatch }, data) {

            if (!data.id || !data.name) return

            const query = await firebase.firestore().collection("creatorContentTags")
              .where("facilityId", "==", this.state.authModule.currentFacility)
              .where("name", "==", data.name.trim().toLowerCase())
              .where(firebase.firestore.FieldPath.documentId(), '!=', data.id).get()

            if (query.size > 0) {
                return {
                    isExistByName: true
                }
            }

            return await firebase.firestore().collection("creatorContentTags").doc(data.id).update({
                name: data.name,
                dateTimeUpdated: firebase.firestore.Timestamp.fromDate(new Date())
            })
              .then(async () => {
                  await dispatch("getContentTags")
                  return { isSuccess: true }
              })
              .catch(() => {
                  return { isSuccess: false }
              })
        },
        async deleteContentTag({ dispatch }, id) {
            if (id?.length <= 0) return;

            const db = firebase.firestore();

            try {
                await db.runTransaction(async (transaction) => {
                    const tagDocRef = db.collection("creatorContentTags").doc(id);
                    const tagSnapshot = await transaction.get(tagDocRef);

                    if (!tagSnapshot.exists) return;

                    const records = await dispatch("isContentTagInUse", id);
                    const updatePromises = [];

                    for (const r of records) {
                        const recordDocRef = db.collection(r.collection).doc(id);
                        const recordSnapshot = await transaction.get(recordDocRef);

                        if (r.collection === "creatorContentTags" && recordSnapshot.exists) {
                            const updateData = {
                                tags: recordSnapshot.data().tags.filter(x => x !== id),
                            };

                            updatePromises.push(transaction.update(recordDocRef, updateData));
                        }
                    }

                    // Delete the tag
                    transaction.delete(tagDocRef);

                    // Wait for all updates to complete
                    await Promise.all(updatePromises);
                });
                await dispatch("getContentTags")
            } catch (error) {
                console.error("Error deleting tag and updating records:", error);
            }
        },
        async isContentTagInUse({ dispatch, state }, tagId) {
            const contents = state.content;

            const records = contents.filter(r => {
                const tags = r.tags || [];
                r.collection = 'creatorContentTags'
                return tags.some(t => t === tagId)
            });

            return records
        }
    },
}

export default contentCreatorModule;